<template>
  <div class="project-container-wrapper">
    <div class="project-container">
      <h4>
        <HKLLeftRight>
          <template #left>
            <span>{{ heading }}</span>
            <i
              class="fa fa-refresh dark flat icon_refresh"
              @click="() => loadProjectTable()"
            />
          </template>
          <template #right>
            <HKLButton
              class="btn-reset"
              action="resetFilter"
              color="darkblue"
              @click="() => resetFilterObject()"
            />
          </template>
        </HKLLeftRight>
      </h4>
      <div class="project-table">
        <table class="hover">
          <thead>
            <tr>
              <th
                v-for="header in itemHeaders"
                :key="header.code"
                :class="header.class"
                @click="() => sortBy(header.code, header.rank)"
              >
                {{ header.name }} {{ orderbyAttr === header.code ? !reverse ? '&#9660;' : '&#9650;' : null }}
              </th>
            </tr>
            <tr>
              <th
                v-for="header in itemHeaders"
                :key="header.code"
              >
                <component
                  :is="header.filterComponent"
                  v-model="filterObject[header.code]"
                  v-bind="header.filterComponentAttrs || {}"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in filteredItems"
              :key="item.id"
              :class="{ 'bold_text': item.isPin }"
            >
              <td>
                <a
                  class="link_text"
                  :class="{ 'text_red': item.highlightNumber }"
                  @click="() => navToProjectPage(item)"
                >{{ item.number }}</a>
              </td>
              <td>
                <a
                  class="link_text"
                  @click="() => navToProjectPage(item)"
                >{{ item.poName }}</a>
                <HKLDocBadge
                  v-if="item.hasNewDoc"
                  :inline="true"
                />
              </td>
              <td>{{ item.site }}</td>
              <td v-if="showProjectInfo(item)">
                {{ item.curSubmission }}
                <HKLPinBadge
                  v-if="item.isPin"
                  :inline="true"
                />
              </td>
              <td v-else>
                {{ $t('emptyValue') }}
              </td>
              <td v-if="showProjectInfo(item)">
                <a
                  class="no-finger"
                  :title="(item.curSubmStatusUpdatedDate && `Status changed on ${dateUtil.formatDate(item.curSubmStatusUpdatedDate, true, false)}`)"
                >{{ item.curSubmStatus || $t('emptyValue') }}</a>
              </td>
              <td v-else>
                {{ $t('emptyValue') }}
              </td>
              <td class="number_text">
                {{ (item.inviteDate && dateUtil.formatDate(item.inviteDate, true, true)) || $t('emptyValue') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters } from 'vuex'
import { computed, getCurrentInstance, unref, ref, onMounted } from 'vue-demi'
import dataTable from '@/controls/DataTable'
import confirmCreate from '@/modules/tenders/ConfirmCreate'
import * as projectUtil from '@/api/projects/util'
import dateUtil from '@/utils/dateUtil'
import nav from '@/utils/navigation'
import ordinalUtils from '@/utils/ordinalUtils'
import { aclHelper } from '@/utils/aclHelper'
import { CONTRACTOR_TYPES } from '@/modules/tenantProjects/contractors/consts'
import { getVendorProjects } from '@/api/projects/request'
import { getSubmStatusLabel } from '@/api/projects/submission'
import { projectStatus, folderStatus, submissionStatus } from '@/utils/locale'
import HKLInput from '@/controls/Input/HKLInput.vue'
import DatePicker from '@/controls/DatePicker.vue'
import useRegionRef from '@/utils/useRegionRef'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import HKLButton from '@/controls/Button/HKLButton.vue'
import HKLDocBadge from '@/controls/Badge/HKLDocBadge.vue'
import HKLPinBadge from '@/controls/Badge/HKLPinBadge.vue'
import { filterValueFn } from '../utils'

const cachedFilterObject = useRegionRef({}, { logoutReset: true })

export default {
  name: 'VendorProjectsTable',
  components: {
    confirmCreate,
    dataTable,
    HKLLeftRight,
    HKLButton,
    HKLDocBadge,
    HKLPinBadge,
  },
  props: {
    heading: {
      type: String,
      default: () => undefined,
    },
    headers: {
      type: String,
      default: () => undefined,
    },
    loadingTable: String,
    model: Array,
    preload: {
      type: Boolean,
      default: false,
    },
    preloadedProjects: {
      type: Array,
      default: () => [],
    },
  },
  setup () {
    const { proxy: that } = getCurrentInstance()
    const itemHeaders = computed(() => [
      {
        name: that.$t('vendorProject.poContract'),
        code: 'number',
        class: 'number-header',
        filterNoCase: true,
      },
      {
        code: 'poName',
        name: that.$t('name'),
        class: 'name-header',
        rank: project => projectUtil.getPurchaseAttrFromObject(project, 'description'),
        filterNoCase: true,
      },
      {
        code: 'site',
        name: that.$t('tender.general.siteId'),
        class: 'site-header',
        filterNoCase: true,
      },
      {
        code: 'curSubmission',
        name: that.$t('vendorProject.currentSubmission'),
        class: 'status-header',
        rank: project => lodash.get(project, 'curSubm.order') || 0 + that.getCompletionPercentage(project)[1],
        onFilterFn: (value, filterValue) => {
          return filterValueFn(value, filterValue, { noCase: true })
        },
      },
      {
        code: 'status',
        name: that.$t('status.name'),
        class: 'status-header',
        rank: project => project.curSubmStatus,
        filterComponent: HKLMultiSelect,
        filterComponentDefaultValue: [],
        filterComponentAttrs: {
          includeAll: true,
          showSearch: true,
          options: [
            'DRAFT',
            'SUBMITTED',
            'ACCEPT',
            'REJECTED',
            'UNACCEPT',
          ],
        },
        onFilterFn: (_, filterValue, __, project) => {
          return filterValueFn(project.curSubmStatus, filterValue)
        },
      },
      {
        code: 'inviteDate',
        name: that.$t('project.createDate'),
        class: 'number_text date-header',
        rank: project => lodash.get(project, 'inviteDate'),
        filterComponent: DatePicker,
        filterComponentAttrs: {
          calendarButton: true,
          calendarButtonIcon: 'fa fa-calendar',
        },
        onFilterFn: (value, filterValue) => {
          const targetDate = dateUtil.getStartOfDay(value)
          const filterDate = dateUtil.getStartOfDay(filterValue)
          return targetDate === filterDate
        },
      },
    ].map((item) => Object.assign({ filterComponent: HKLInput, rank: (val) => val[item.code] }, item)))
    const initFilterObject = () => {
      return unref(itemHeaders).reduce((obj, { code, filterComponentDefaultValue }) => {
        obj[code] = filterComponentDefaultValue || ''
        return obj
      }, {})
    }
    onMounted(() => {
      if (Object.keys(cachedFilterObject.value).length > 0) return
      cachedFilterObject.value = initFilterObject()
    })
    const reverse = ref(0)
    const orderbyAttr = ref(() => lodash.last(unref(itemHeaders)).code)
    const orderRank = ref(() => lodash.last(unref(itemHeaders)).rank)

    const items = ref([])
    const filteredItems = computed(() => {
      const parsedList = unref(that.orderedItems)
        .filter((item, index) => {
          return unref(itemHeaders).every(({ code, onFilterFn, filterNoCase = false }) => {
            const filterValue = unref(cachedFilterObject)[code]
            const value = item[code]
            const result = filterValueFn(value, filterValue, {
              noCase: filterNoCase,
              customDiffFn: typeof onFilterFn === 'function' ? (a, b) => onFilterFn(a, b, index, item) : undefined,
            })
            return result
          })
        })
      return parsedList
    })
    return {
      items,
      filteredItems,
      itemHeaders,
      orderbyAttr,
      orderRank,
      reverse,
      filterObject: cachedFilterObject,
      resetFilterObject () {
        cachedFilterObject.value = initFilterObject()
        reverse.value = 0
        orderbyAttr.value = () => lodash.last(unref(itemHeaders)).code
        orderRank.value = () => lodash.last(unref(itemHeaders)).rank
      },
    }
  },
  data () {
    return {
      loadingProjectTable: false,
      activeStatus: projectStatus.ACTIVE,
      dateUtil,
      ordinalUtils,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser']),
    orderedItems () {
      const result = this.items
        .map((project) => {
          // Current Submission should be the first DRAFT/last submission in reverse order
          let curSubm = null
          if (project.submissions.length !== 0) {
            curSubm = project.submissions.concat().sort((a, b) => {
              if (a.order < b.order) { return -1 }
              if (a.order > b.order) { return 1 }
              return 0
            }).find(submission => submission.status === submissionStatus.DRAFT)
            curSubm = curSubm || project.submissions[0]
          }
          project.curSubm = curSubm
          project.curSubmStatus = (project.curSubm && this.getSubmStatusLabel(project.curSubm)[0]) || this.$t('emptyValue')
          return project
        })
        .filter((project) => this.filterProject(project))
      const group = lodash.groupBy(result, 'projectType')
      const defaultList = [
        (group['ProjectRep'] || []).filter((project) => project.isPin),
        (group['ProjectRep'] || []).filter((project) => !project.isPin && project.hasNewDoc),
        group['ProjectVendor'] || [],
        group['ProjectCon'] || [],
        (group['ProjectRep'] || []).filter((project) => !project.isPin && !project.hasNewDoc),
      ]
      const list = (typeof this.orderbyAttr === 'string' ? [
        defaultList.shift(),
        defaultList.flat(),
      ] : defaultList)
        .map((list) => lodash.orderBy(list, [
          this.orderRank || this.orderbyAttr,
          (item) => lodash.get(item, 'curSubm.createdAt', -1),
        ], [this.reverse ? 'asc' : 'desc', 'desc']))
      return list
        .flat()
        .map((project) => this.parseProject(project))
    },
    showProjectsTable () {
      return this.hasProjectsAccess || this.hasWorkPermitAccess
    },
    hasProjectsAccess () {
      return aclHelper.hasProjectsAccess(true)
    },
    hasWorkPermitAccess () {
      return aclHelper.hasWorkPermitAccess(true)
    },
  },
  watch: {
    orderedItems () {
      this.$emit('update:model', this.orderedItems)
    },
  },
  async mounted () {
    if (this.showProjectsTable) {
      if (this.preload) {
        setTimeout(() => {
          this.items = this.preloadedProjects
        }, 100)
      } else {
        await this.loadProjectTable()
      }
    }
  },
  methods: {
    navToProjectPage (item) {
      nav.toPage('vendorProjectDetail', { id: item.id })
    },
    isProjectAccessible (project) {
      if (project.projectType === 'ProjectRep') return true
      const validateDlpInCompleted = projectUtil.validateDlpInCompleted(project)
      return project.status === this.activeStatus || validateDlpInCompleted
    },
    getCompletionPercentage (item) {
      const purchase = projectUtil.getPurchase(item)
      if (purchase && purchase.amount > 0) {
        let completedAmount
        if (item.curSubm) {
          completedAmount = (item.submissions || [])
            .slice(item.submissions.indexOf(item.curSubm))
            .reduce((sum, submission) => sum + submission.completedAmount, 0)
        }
        if (completedAmount) {
          const purchaseAmount = projectUtil.getPurchaseAttrFromObject(item, 'amount')
          const denominator = (((purchaseAmount || 0) - (lodash.get(item, 'deduction.amount') || 0)))
          const percent = denominator - 0 ? completedAmount * 100 / denominator : 0
          return [(percent).toFixed(2) + '%', percent]
        }
      }
      return ['--', 0]
    },

    filterProject (project = {}) {
      return this.isProjectAccessible(project) ? project : null
    },
    parseProject (project = {}) {
      const shownAttrs = {}
      shownAttrs.id = project.id
      shownAttrs.number = projectUtil.getPurchaseNumber(project.numberType, projectUtil.getPurchase(project)) || this.$t('emptyValue')
      shownAttrs.poName = projectUtil.getPurchaseAttrFromObject(project, 'description') || this.$t('emptyValue')
      shownAttrs.status = project.status
      shownAttrs.curSubmStatus = project.curSubmStatus
      shownAttrs.curSubmStatusUpdatedDate = lodash.get(project.curSubm, 'statusUpdatedDate')
      shownAttrs.curSubmOrder = lodash.get(project.curSubm, 'order')
      shownAttrs.inviteDate = project.inviteDate || this.$t('emptyValue')
      shownAttrs.rawValue = project
      shownAttrs.isMainContractor = this.isMainContractor(project)
      shownAttrs.isSubContractor = this.isSubContractor(project)
      shownAttrs.site = projectUtil.getPurchase(project).siteId
      shownAttrs.curSubmission = this.formatCurSub(project)
      shownAttrs.isPin = !!project.isPin
      shownAttrs.hasNewDoc = !!project.hasNewDoc
      shownAttrs.highlightNumber = project.projectType === 'ProjectRep'
      return shownAttrs
    },
    // @TODO gino change to document level
    getDocumentsAccepted (item) {
      const approvedDocs = (item.folders || []).filter(folder => folder.status === folderStatus.APPR).length
      const totalDocs = item.folders.length
      return [`${approvedDocs} / ${totalDocs}`, approvedDocs, totalDocs]
    },
    async loadProjectTable () {
      this.loadingProjectTable = true
      this.items = []
      const projectRawList = await getVendorProjects({ status: projectStatus.ACTIVE, startOfToday: dateUtil.getTodayStartDateObject() })
      this.items = projectRawList
      this.loadingProjectTable = false
    },
    formatCurSub (project) {
      const curSubmOrder = lodash.get(project.curSubm, 'order')
      const order = (curSubmOrder && ordinalUtils.getOrdinalFor(curSubmOrder, true)) || this.$t('emptyValue')
      const completion = `(${this.getCompletionPercentage(project)[0]})`

      return `${order} ${completion}`
    },
    sortBy (attr, rank) {
      if (this.orderbyAttr === attr) {
        this.reverse = !this.reverse
      }
      this.orderbyAttr = attr
      this.orderRank = rank
    },
    isMainContractor (item) {
      // If current user is main user of item, can find the value of mainContractor, or mainContractor will be undefiend
      // FIXME The data should be limited in backend.
      const mainContractor = lodash.get(item, 'contractors', []).find(contractor => contractor.type === CONTRACTOR_TYPES.MAIN)
      return !!mainContractor
    },
    isSubContractor (item) {
      const subContractor = lodash.get(item, 'contractors', []).find(contractor => contractor.type && contractor.type !== CONTRACTOR_TYPES.MAIN)
      return subContractor && this.currentUser.externalId !== item.vendorId
    },
    showProjectInfo (item) {
      return (this.hasProjectsAccess && !item.isSubContractor) || item.isMainContractor
    },
    getSubmStatusLabel: (...args) => getSubmStatusLabel(...args, true),
  },
}
</script>

<style scoped src="@/assets/styles/dashboard/datatable.scss"></style>

<style scoped>

.project-table {
  border-radius: 4px;
  background: white;
}

tr:nth-child(even) {
  background-color: #f8f8f8;
}

tr {
  border: solid 1px #e0e0e0;
}

th {
  background-color: #f8f8f8;
  font-family: Verdana;
  font-size: 14px;
  font-weight: bold;
  color: #0C3A58;
  text-transform: uppercase;
  height: 48px;
}

table {
  border: solid 1px #e0e0e0;
  border-spacing: 0px;
  border-collapse: collapse;
}

td {
  font-family: Verdana;
  font-size: 14px;
  color: #0C3A58;
}

.icon_refresh:hover {
  cursor: pointer;
}

.icon_refresh:active {
  color: #468dc5;
}

.link_text{
  text-decoration: underline;
}

.number_text{
  text-align: right;
}

.number-header {
  width: 10%
}
.name-header {
  width: 40%;
}
.site-header {
  width: 10%
}
.status-header {
  width: 15%
}
.date-header {
  width: 15%
}
.no-finger {
  cursor: default;
}
a.no-finger, a.no-finger:visited, a.no-finger:hover, a.no-finger:active {
  color: inherit;
}

h4 {
  &:has(.btn-reset) {
    padding-bottom: 8px;
  }
  & > .hkl-left-right {
    align-items: center;
    span + .icon_refresh {
      margin-left: 5px;
    }
    :has(.btn-reset) {
      padding-bottom: 8px;
    }
  }
}
</style>
